import { t } from 'i18next';
import { blue } from 'theme/colors';
import { Box, SvgIcon, Tooltip } from '@mui/material';
import { IconCircleCheck, IconInfoCircle, IconXboxX } from '@tabler/icons-react';
import { ActionButton } from '@atoms';
import { useUser } from '@hooks';
import { PendingCustomer } from '../../data/useGetPendingCustomers';
import { SelectedPendingCustomer } from '../../data/useResolvePendingCustomer/hooks/useResolvePendingCustomer';

/**
 * Approve / reject buttons for pending customer rows
 * @param customer - Pending customer row
 * @param callback - Function triggered on action click
 */
export default function ActionButtons({
  customer,
  callback,
}: {
  customer: PendingCustomer;
  callback: (c: SelectedPendingCustomer) => void;
}) {
  const { user } = useUser();
  const isOwnRequest = user?.email?.toLowerCase() === customer?.createdBy?.toLowerCase();
  const isPending = customer.status === 'PENDING';
  const showOwnRequestInfo = isPending && isOwnRequest;
  const showActions = isPending && !isOwnRequest;

  /**
   * Prevents row click navigation when interacting with actions
   * @param event - Click event
   */
  const handleStopPropagation = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <Box display={'flex'} justifyContent={'space-around'} onClick={handleStopPropagation}>
      {showOwnRequestInfo && (
        <Tooltip title={t('PENDING_CUSTOMERS:OWN_REQUEST_INFO')} arrow>
          <Box display='flex' alignItems='center'>
            <SvgIcon fontSize='medium'>
              <IconInfoCircle color={blue.main} />
            </SvgIcon>
          </Box>
        </Tooltip>
      )}
      <ActionButton
        {...{
          tooltip: 'COMMON:APPROVE',
          icon: <IconCircleCheck color={blue.main} />,
          callback: () =>
            callback({
              id: customer.id,
              customerName: customer.customerName,
              cuit: customer.cuit,
              action: 'APPROVE',
            }),
          show: showActions,
        }}
      />
      <ActionButton
        {...{
          tooltip: 'COMMON:REJECT',
          icon: <IconXboxX color={blue.main} />,
          callback: () =>
            callback({
              id: customer.id,
              customerName: customer.customerName,
              cuit: customer.cuit,
              action: 'REJECT',
            }),
          show: showActions,
        }}
      />
    </Box>
  );
}
